// ========================================
// Dashboard View — Panel de Control Ejecutivo (Holded Style)
// ========================================

const DashboardView = {
    viewMode: 'day',
    searchQuery: '',
    _stats: null,
    _admissionsFilter: 'pending',

    render() {
        // Esqueleto con la misma silueta que el contenido real (hero + 4 KPIs), no un spinner genérico
        const skeleton = `
            <div class="view-container dashboard-view" style="padding-top: 1rem;">
                <div id="dashboard-content">
                    <div class="dnr-proposal">
                        <div class="dnr-skel dnr-skel-hero"></div>
                        <div class="dnr-grid">
                            <div class="dnr-skel dnr-skel-card"></div>
                            <div class="dnr-skel dnr-skel-card"></div>
                            <div class="dnr-skel dnr-skel-card"></div>
                            <div class="dnr-skel dnr-skel-card"></div>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Retornar skeleton y cargar datos async
        setTimeout(() => this._loadAndRender(), 0);
        return skeleton;
    },

    async _loadAndRender() {
        const contentArea = document.getElementById('dashboard-content');
        try {
            const token = AuthManager.getCsrfToken();
            const response = await fetch('/api/dashboard/stats', {
                headers: { 'X-SICAF-CSRF': token }
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.error);
            this._stats = data;

            if (!contentArea) return;
            contentArea.style.width = '100%';
            // Se consume una sola vez: solo esta primera carga tras el login anima
            // hero+widgets (ver App.justLoggedIn en js/app.js). Cualquier otra vez que
            // el usuario vuelva al dashboard durante la sesión, se renderiza sin animar.
            const animateEntrance = typeof App !== 'undefined' && !!App.justLoggedIn;
            if (typeof App !== 'undefined') App.justLoggedIn = false;
            SafeHTML.set(contentArea, this._renderContent(data, animateEntrance));
            this._initAsync();
        } catch (err) {
            console.error('Error cargando dashboard stats:', err);
            if (contentArea) {
                SafeHTML.set(contentArea, `
                    <div class="dnr-proposal">
                        <div class="dnr-card" style="text-align:center; padding:3rem 2rem;">
                            <i class="fas fa-triangle-exclamation" style="font-size:1.8rem; color:var(--dnr-danger); margin-bottom:0.75rem; display:block;"></i>
                            <div style="font-weight:800; font-size:1rem; color:var(--dnr-ink); margin-bottom:0.35rem;">No se pudieron cargar las métricas del panel</div>
                            <div style="font-size:0.85rem; color:var(--dnr-ink-mute); margin-bottom:1.25rem;">Comprueba tu conexión e inténtalo de nuevo.</div>
                            <button type="button" class="dnr-quick-btn" style="flex-direction:row; width:auto; display:inline-flex; padding:0.6rem 1.25rem; margin:0 auto;" ${UIEvents.attr("click", function(event,captured){DashboardView._loadAndRender();}, [])}>
                                <i class="fas fa-rotate-right"></i> Reintentar
                            </button>
                        </div>
                    </div>
                `);
            }
        }
    },

    _renderContent(data, animateEntrance = false) {
        return `<div id="dnr-proposal-host">${this._renderProposal(data, animateEntrance)}</div>`;
    },

    // ── Inicio, diseño A2 (2026-10-25) ─────────────────────────────────────────
    // Lo pendiente arriba, el dinero en una franja y después el día, admisiones,
    // académico, contactos, personal, logística, alumnado, semana, mensajes y accesos.
    // Cada bloque es un widget personalizable (CategoryDashboards, panel 'dashboard');
    // estilos en assets/css/views/inicio.css bajo .inicio-view.

    _renderProposal(data, animateEntrance = false) {
        this._lastProposalData = data;
        const { stats } = data;
        const user = AuthManager.getCurrentUser();
        const name = user ? String(user.name || user.username || '').split(' ')[0] : '';
        const now = new Date();
        const hour = now.getHours();
        const greeting = hour < 14 ? 'Buenos días' : hour < 21 ? 'Buenas tardes' : 'Buenas noches';
        const day = now.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        const course = stats.financials?.academicYearLabel || this._courseLabel(now);
        const isEditMode = CategoryDashboards.isEditMode('dashboard');
        const widgetsConfig = CategoryDashboards.getWidgetsConfig('dashboard');
        this._needWeek();
        this._needPayrollMonths(() => { if (App.currentView === 'dashboard') this.reRenderProposal(); });

        return `
        <div class="dnr-proposal inicio-view${animateEntrance ? ' entrance-anim' : ''}">
            <section class="inv-head">
                <div class="inv-head-text">
                    <h1 class="inv-title">${greeting}${name ? ', ' + Utils.sanitizeHTML(name) : ''}</h1>
                    <p class="inv-sub">${Utils.sanitizeHTML(day.charAt(0).toUpperCase() + day.slice(1))} · Curso ${Utils.sanitizeHTML(course.replace('/', ' / '))} · ${stats.professors.total} docentes · ${stats.students.total} alumnos registrados</p>
                </div>
                <button type="button" id="btn-cog-dashboard" class="inv-btn" style="display: ${isEditMode ? 'none' : 'flex'};" ${UIEvents.attr('click', () => CategoryDashboards.toggleWidgetDrawer('dashboard'))}>
                    <i class="fas fa-sliders" aria-hidden="true"></i> Personalizar
                </button>
            </section>

            ${this._renderDrawer(widgetsConfig, isEditMode)}

            ${this._renderEditBanner(isEditMode)}

            <div id="widgets-grid-dashboard" class="dnr-widget-grid inv-grid">
                ${widgetsConfig.map((w, i) => this._renderDashboardWidget(w, isEditMode, data, i)).join('')}
            </div>
        </div>`;
    },

    // Shared by every panel built like the Inicio (also the Panel Académico).
    _renderDrawer(widgetsConfig, isEditMode, dashboardId = 'dashboard', title = 'Personalizar el Inicio') {
        return `
            <div id="widget-drawer-overlay-${dashboardId}" class="inv-drawer-overlay" ${UIEvents.attr('click', () => CategoryDashboards.toggleWidgetDrawer(dashboardId))} style="display:none; opacity:0;"></div>
            <div id="widget-drawer-${dashboardId}" class="inv-drawer" role="dialog" aria-labelledby="inv-drawer-title-${dashboardId}" style="transform:translateX(100%); visibility:hidden;">
                <div class="inv-drawer-head">
                    <div>
                        <h2 id="inv-drawer-title-${dashboardId}">${Utils.sanitizeHTML(title)}</h2>
                        <p>Elige qué bloques ves y cómo se ordenan.</p>
                    </div>
                    <button type="button" class="inv-icon-btn" aria-label="Cerrar" ${UIEvents.attr('click', () => CategoryDashboards.toggleWidgetDrawer(dashboardId))}><i class="fas fa-times" aria-hidden="true"></i></button>
                </div>
                <div class="inv-drawer-body">
                    <div class="inv-drawer-mode">
                        <div>
                            <strong>Ordenar y cambiar el ancho</strong>
                            <span>Arrastra los bloques y elige 1, 2 o 3 columnas.</span>
                        </div>
                        <button id="btn-edit-toggle-${dashboardId}" type="button" class="inv-btn ${isEditMode ? 'is-danger' : 'is-primary'}" ${UIEvents.attr('click', () => CategoryDashboards.toggleEditMode(dashboardId))}>
                            <i class="fas ${isEditMode ? 'fa-check-circle' : 'fa-edit'}" aria-hidden="true"></i> ${isEditMode ? 'Finalizar' : 'Editar'}
                        </button>
                    </div>
                    <h3 class="inv-label">Bloques</h3>
                    ${widgetsConfig.map(w => `
                        <div class="inv-drawer-item">
                            <label class="inv-switch">
                                <input type="checkbox" ${w.visible ? 'checked' : ''} ${UIEvents.attr('change', () => CategoryDashboards.toggleWidgetVisibility(dashboardId, w.id))}>
                                <span id="toggle-track-${Utils.sanitizeHTML(w.id)}" style="background:${w.visible ? '#0E6B62' : '#C4D0CA'};"></span>
                                <i id="toggle-thumb-${Utils.sanitizeHTML(w.id)}" style="left:${w.visible ? '19px' : '3px'};"></i>
                            </label>
                            <div class="inv-drawer-item-text">
                                <strong>${Utils.sanitizeHTML(w.name)}</strong>
                                <span>${Utils.sanitizeHTML(CategoryDashboards._widgetDescriptions[w.id] || '')}</span>
                                <div id="span-selector-${Utils.sanitizeHTML(w.id)}" class="inv-span-row" style="display:${w.visible ? 'flex' : 'none'};">
                                    <span>Ancho:</span>
                                    ${[1, 2, 3].map(s => `<button type="button" class="span-btn inv-span-btn ${w.span === s ? 'is-on' : ''}" data-span="${s}" ${UIEvents.attr('click', () => CategoryDashboards.setWidgetSpan(dashboardId, w.id, s))}>${s}</button>`).join('')}
                                </div>
                            </div>
                            <span id="dropdown-span-${Utils.sanitizeHTML(w.id)}" hidden>${w.span}</span>
                        </div>`).join('')}
                </div>
                <div class="inv-drawer-foot">
                    <button type="button" class="inv-btn" ${UIEvents.attr('click', () => CategoryDashboards.resetWidgetConfig(dashboardId))}>
                        <i class="fas fa-undo" aria-hidden="true"></i> Volver al orden de siempre
                    </button>
                </div>
            </div>`;
    },

    _renderEditBanner(isEditMode, dashboardId = 'dashboard', what = 'el Inicio') {
        return `
            <div id="edit-banner-${dashboardId}" class="inv-edit-banner" style="display: ${isEditMode ? 'flex' : 'none'};">
                <div>
                    <strong>Personalizando ${Utils.sanitizeHTML(what)}</strong>
                    <span>Arrastra los bloques para ordenarlos y usa 1, 2 o 3 para cambiar su ancho.</span>
                </div>
                <button type="button" class="inv-btn" ${UIEvents.attr('click', () => CategoryDashboards.toggleEditMode(dashboardId))}>
                    <i class="fas fa-check" aria-hidden="true"></i> Guardar y listo
                </button>
            </div>`;
    },

    reRenderProposal() {
        const host = document.getElementById('dnr-proposal-host');
        if (!host || !this._lastProposalData) return;
        SafeHTML.replace(host, `<div id="dnr-proposal-host">${this._renderProposal(this._lastProposalData)}</div>`);
        this._initAsync();
        CategoryDashboards.attachDragAndDrop('dashboard');
    },

    // Lo que llega aparte: asistencia, memorias y entrevistas de hoy.
    _initAsync() {
        this._loadAttendance();
        this._loadMemorias();
        this._loadInterviews();
    },

    // ── Utilidades ───────────────────────────────────────────────────────────

    _can(section) {
        return typeof RolePermissions === 'undefined' || RolePermissions.canViewSection(section);
    },

    _iso(d) {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },

    _courseLabel(d) {
        const start = d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1;
        return `${start}/${start + 1}`;
    },

    _money(n) {
        return `${Math.round(Number(n) || 0).toLocaleString('es-ES')} €`;
    },

    _initials(name) {
        return String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
    },

    _monday(d = new Date()) {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));
    },

    // Esta semana entera, por si cruza de mes: al llegar las clases se vuelve a pintar.
    _needWeek() {
        if (typeof FileStorage === 'undefined' || !FileStorage.needClasses) return;
        const monday = this._monday(), sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
        FileStorage.needClasses(monday, sunday, () => { if (App.currentView === 'dashboard') this.reRenderProposal(); });
    },

    _classesOn(iso) {
        if (typeof ClassService === 'undefined') return [];
        return ClassService.getClasses().filter(c => c.startDate === iso)
            .sort((a, b) => String(a.startTime || '').localeCompare(String(b.startTime || '')));
    },

    _classProfessorId(c) {
        if (c.professorId) return c.professorId;
        const subject = c.subjectId && typeof DataManager !== 'undefined' ? DataManager.getSubject(c.subjectId) : null;
        return subject ? subject.professorId : null;
    },

    _professorName(id) {
        const p = id && typeof DataManager !== 'undefined' ? DataManager.getProfessor(id) : null;
        return p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : '';
    },

    _timeAgo(date) {
        const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
        if (days <= 0) {
            const hours = Math.floor((Date.now() - new Date(date).getTime()) / 3600000);
            return hours <= 0 ? 'ahora' : `hace ${hours} h`;
        }
        return days === 1 ? 'ayer' : `hace ${days} días`;
    },

    _head(title, link) {
        return `
            <div class="inv-card-head">
                <h2 class="inv-card-title">${title}</h2>
                ${link ? `<button type="button" class="inv-link" ${UIEvents.attr('click', link.go)}>${link.label} <span aria-hidden="true">→</span></button>` : ''}
            </div>`;
    },

    _stat(n, label, tone = '') {
        return `<div class="inv-stat ${tone}"><span class="inv-stat-n">${n}</span><span class="inv-stat-l">${label}</span></div>`;
    },

    // ── Bloques ─────────────────────────────────────────────────────────────

    _renderDashboardWidget(w, isEditMode, data, i) {
        const builders = {
            'inicio-pendiente': () => this._wPendiente(data),
            'inicio-dinero': () => this._wDinero(data),
            'inicio-hoy': () => this._wHoy(),
            'inicio-admisiones': () => this._wAdmisiones(data),
            'inicio-academico': () => this._wAcademico(),
            'inicio-contactos': () => this._wContactos(data),
            'inicio-docencia': () => this._wDocencia(),
            'inicio-logistica': () => this._wLogistica(data),
            'inicio-alumnado': () => this._wAlumnado(),
            'inicio-semana': () => this._wSemana(),
            'inicio-mensajes': () => this._wMensajes(),
            'inicio-festivos': () => this._wFestivos(),
            'inicio-accesos': () => this._wAccesos()
        };
        if (!builders[w.id]) return '';
        return this._dnrWidgetShell(w, isEditMode, builders[w.id](), i);
    },

    _pendingItems(data) {
        const { stats, warehouse } = data;
        const items = [];
        if (this._can('pre-matricula')) items.push({ n: stats.enrollments.pending, label: 'Solicitudes por revisar', where: 'Admisiones',
            go: () => { if (typeof StudentView !== 'undefined') StudentView.currentSection = 'admissions'; App.navigate('alumnado/admissions'); } });
        if (this._can('leads')) items.push({ n: stats.leads.pending, label: 'Contactos sin contestar', where: 'Contactos', go: () => App.navigate('leads') });
        const payroll = this._can('nominas') ? this._payrollPending() : null;
        if (payroll) items.push({ n: payroll.rows.length, label: 'Nóminas sin subir', where: payroll.label, go: () => App.navigate('profesores/nominas') });
        if (typeof JustificantesView !== 'undefined' && JustificantesView.isReviewer()) {
            items.push({ n: JustificantesView.all().filter(d => !d.reviewedAt).length, label: 'Justificantes sin abrir', where: 'Personal y alumnado', go: () => App.navigate('justificantes') });
        }
        if (this._can('almacen')) {
            items.push({ n: warehouse.pendingRequestsCount, label: 'Material por entregar', where: 'Almacén', go: () => App.navigate('almacen') });
            items.push({ n: warehouse.pendingOrdersCount, label: 'Compras por firmar', where: 'Almacén', go: () => App.navigate('almacen') });
        }
        return items;
    },

    _wPendiente(data) {
        const open = this._pendingItems(data).filter(item => item.n > 0);
        const total = open.reduce((sum, item) => sum + item.n, 0);
        return `
            <div class="inv-card-head">
                <h2 class="inv-card-title">Pendiente hoy ${total ? `<span class="inv-badge is-warn">${total}</span>` : ''}</h2>
                <button type="button" class="inv-link" ${UIEvents.attr('click', () => App.navigate('acciones-pendientes'))}>Ver todo <span aria-hidden="true">→</span></button>
            </div>
            ${open.length ? `
                <div class="inv-pend-grid">
                    ${open.map(item => `
                        <button type="button" class="inv-pend" ${UIEvents.attr('click', item.go)}>
                            <span class="inv-pend-n">${item.n}</span>
                            <span class="inv-pend-text"><strong>${item.label}</strong><span>${Utils.sanitizeHTML(item.where)}</span></span>
                        </button>`).join('')}
                </div>` : '<p class="inv-empty">Todo al día: no hay nada esperando.</p>'}`;
    },

    _wDinero(data) {
        const financials = data.stats.financials;
        const marginRatio = financials && financials.sales > 0 ? Math.max(0, Math.min(100, Math.round((financials.profit / financials.sales) * 100))) : 0;
        // Accounts with only the delegated 'dashboard' module do not receive financial figures.
        if (!financials) {
            return `${this._head('Resumen financiero')}<p class="inv-empty"><i class="fas fa-lock" aria-hidden="true"></i> Las cifras económicas requieren también el permiso de Nóminas.</p>`;
        }
        const pct = (part, whole) => whole > 0 ? Math.max(0, Math.min(100, Math.round(part / whole * 100))) : 0;
        const collected = pct(financials.receivedCollections, financials.sales);
        const toPrematricula = () => App.navigate('pre-matricula');
        const cells = [
            { label: 'Previsión matrículas', value: financials.sales, sub: `${collected} % cobrado · ${financials.activePreRegistrations} alumnos`, bar: collected, go: toPrematricula },
            { label: 'Cobrado', value: financials.receivedCollections, sub: `de ${this._money(financials.sales)}`, bar: collected, tone: 'is-ok', go: toPrematricula },
            { label: 'Pendiente de cobro', value: financials.pendingCollections, sub: `${pct(financials.pendingCollections, financials.sales)} % del curso`, bar: pct(financials.pendingCollections, financials.sales), tone: 'is-warn', barTone: 'is-warn', go: toPrematricula },
            { label: 'Gastos de personal', value: financials.expenses, sub: `Clases ${this._money(financials.teachingCost ?? financials.expenses)} · Fijos ${this._money(financials.fixedCost ?? 0)}`,
              title: `Curso ${financials.academicYearLabel}: docencia ${this._money(financials.teachingCost)} (${(financials.professorHours || 0).toLocaleString('es-ES')} h de clase) y sueldos fijos ${this._money(financials.fixedCost)}`,
              bar: pct(financials.expenses, financials.sales), barTone: 'is-mute', go: () => App.navigate('profesores/nominas') },
            { label: 'Margen', value: financials.profit, sub: `Curso ${financials.academicYearLabel}`, bar: marginRatio, tone: financials.profit >= 0 ? 'is-ok' : 'is-bad',
              go: AuthManager.isAdmin() ? () => this.openFinancialConfigModal() : toPrematricula }
        ];
        return `
            <div class="inv-money">
                ${cells.map(c => `
                    <button type="button" class="inv-money-cell" ${c.title ? `title="${Utils.sanitizeHTML(c.title)}"` : ''} ${UIEvents.attr('click', c.go)}>
                        <span class="inv-label">${c.label}</span>
                        <span class="inv-money-value ${c.tone || ''}">${this._money(c.value)}</span>
                        <span class="inv-money-sub">${Utils.sanitizeHTML(c.sub)}</span>
                        <span class="inv-bar ${c.barTone || ''}" aria-hidden="true"><i style="width:${c.bar}%"></i></span>
                    </button>`).join('')}
            </div>
            ${AuthManager.isAdmin() ? `<button type="button" class="inv-link inv-money-rates" ${UIEvents.attr('click', () => this.openFinancialConfigModal())}><i class="fas fa-sliders" aria-hidden="true"></i> Tarifas</button>` : ''}`;
    },

    _hoyRows() {
        const now = new Date(), today = this._iso(now);
        const nowHm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        const rows = this._classesOn(today).map(c => {
            const subject = typeof SubjectService !== 'undefined' ? SubjectService.getSubject(c.subjectId) : null;
            const formation = typeof FormationService !== 'undefined' ? FormationService.getFormation(c.formationId) : null;
            const professor = this._professorName(this._classProfessorId(c));
            const end = c.endTime || c.startTime;
            const state = end && end <= nowHm ? 'past' : c.startTime && c.startTime <= nowHm ? 'now' : '';
            return { time: c.startTime || '', kind: 'class', state, title: subject ? subject.name : 'Clase',
                sub: [formation ? (formation.commercialName || formation.name) : '', c.classroom, professor].filter(Boolean).join(' · '),
                tag: state === 'now' ? 'Ahora' : state === 'past' ? 'Terminada' : (c.classroom || ''),
                go: () => CategoryDashboards.goToClassSchedule(c.formationId || '', c.subjectId || '', formation ? formation.name : '', subject ? subject.name : '') };
        });
        for (const s of this._todayInterviews || []) {
            rows.push({ time: s.interviewTime, kind: 'interview', state: s.interviewTime < nowHm ? 'past' : '', title: 'Entrevista de admisión',
                sub: [`${s.firstName || ''} ${s.lastName1 || ''}`.trim(), s.courseName].filter(Boolean).join(' · '), tag: 'Entrevista',
                go: () => App.navigate('entrevistas') });
        }
        return rows.sort((a, b) => a.time.localeCompare(b.time));
    },

    _hoySummary(rows) {
        const classes = rows.filter(r => r.kind === 'class');
        const teachers = new Set(this._classesOn(this._iso(new Date())).map(c => this._classProfessorId(c)).filter(Boolean)).size;
        const interviews = rows.length - classes.length;
        return [`${classes.length} ${classes.length === 1 ? 'clase' : 'clases'}`, `${teachers} ${teachers === 1 ? 'docente' : 'docentes'}`,
            interviews ? `${interviews} ${interviews === 1 ? 'entrevista' : 'entrevistas'}` : ''].filter(Boolean).join(' · ');
    },

    _hoyList(rows) {
        if (!rows.length) return '<p class="inv-empty">Hoy no hay clases ni entrevistas.</p>';
        return rows.map(r => `
            <button type="button" class="inv-hoy-row ${r.state === 'past' ? 'is-past' : ''}" ${UIEvents.attr('click', r.go)}>
                <span class="inv-hoy-time ${r.state === 'now' ? 'is-now' : ''}">${Utils.sanitizeHTML(r.time)}</span>
                <span class="inv-dot ${r.kind === 'interview' ? 'is-interview' : ''}" aria-hidden="true"></span>
                <span class="inv-hoy-text"><strong>${Utils.sanitizeHTML(r.title)}</strong><span>${Utils.sanitizeHTML(r.sub)}</span></span>
                ${r.tag ? `<span class="inv-pill ${r.state === 'now' ? 'is-ok' : r.kind === 'interview' ? 'is-violet' : ''}">${Utils.sanitizeHTML(r.tag)}</span>` : ''}
            </button>`).join('');
    },

    _wHoy() {
        const rows = this._hoyRows();
        return `
            <div class="inv-card-head">
                <h2 class="inv-card-title">Hoy en el centro <span class="inv-muted" id="inv-hoy-summary">${this._hoySummary(rows)}</span></h2>
                <button type="button" class="inv-link" ${UIEvents.attr('click', () => App.navigate('calendario-formacion'))}>Programación <span aria-hidden="true">→</span></button>
            </div>
            <div class="inv-hoy-list" id="inv-hoy-list">${this._hoyList(rows)}</div>`;
    },

    // Entrevistas con fecha de hoy (misma fuente que Planificación de Entrevistas).
    async _loadInterviews() {
        if (!this._can('entrevistas') || this._interviewsDay === this._iso(new Date())) return;
        const epoch = AuthManager._epoch;
        try {
            const res = await fetch('/api/pre-registration', { headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() } });
            const result = await res.json();
            if (epoch !== AuthManager._epoch || !res.ok || !result.success) return;
            const today = this._iso(new Date());
            this._interviewsDay = today;
            this._todayInterviews = (result.preRegistrations || []).filter(s => s.status === 'entrevista_solicitada' && s.interviewDate === today && s.interviewTime
                && !s.isProvisional && !(s.firstName === 'PLAZA' && s.lastName1 === 'PENDIENTE'));
            const list = document.getElementById('inv-hoy-list');
            if (!list || !this._todayInterviews.length) return;
            const rows = this._hoyRows();
            SafeHTML.set(list, this._hoyList(rows));
            const summary = document.getElementById('inv-hoy-summary');
            if (summary) summary.textContent = this._hoySummary(rows);
        } catch (e) {
            console.error('Inicio: no se pudieron cargar las entrevistas de hoy', e);
        }
    },

    _admissionsList(data) {
        const filter = this._admissionsFilter || 'pending';
        const all = data.recentEnrollments || [];
        const list = filter === 'pending' ? all.filter(e => e.status === 'pending' || e.status === 'interview') : all.filter(e => e.status === filter);
        const empty = { pending: 'No hay solicitudes por revisar.', approved: 'No hay solicitudes aprobadas.', rejected: 'No hay solicitudes rechazadas.' };
        const labels = { pending: 'Por revisar', interview: 'Entrevista', payment: 'Pago', approved: 'Aprobada', rejected: 'Rechazada' };
        const tones = { pending: 'is-warn', interview: 'is-violet', payment: 'is-warn', approved: 'is-ok', rejected: 'is-bad' };
        if (!list.length) return `<p class="inv-empty">${empty[filter]}</p>`;
        return list.slice(0, 6).map(e => {
            const fullName = [e.nombre, e.apellidos].filter(Boolean).join(' ') || e.email || 'Solicitante';
            const go = e.status === 'approved'
                ? () => DashboardView.openStudentFromEnrollment(e.id, e.studentId || '')
                : () => { const sec = e.status === 'rejected' ? 'rejected' : 'admissions'; if (typeof StudentView !== 'undefined') StudentView.currentSection = sec; App.navigate('alumnado/' + sec); };
            return `
                <button type="button" class="inv-person" ${UIEvents.attr('click', go)}>
                    <span class="inv-avatar is-violet" aria-hidden="true">${Utils.sanitizeHTML(this._initials(fullName))}</span>
                    <span class="inv-person-text"><strong>${Utils.sanitizeHTML(fullName)}</strong><span>${Utils.sanitizeHTML([e.tipoFormacion, new Date(e.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })].filter(Boolean).join(' · '))}</span></span>
                    <span class="inv-pill ${tones[e.status] || ''}">${labels[e.status] || Utils.sanitizeHTML(e.status)}</span>
                </button>`;
        }).join('');
    },

    _wAdmisiones(data) {
        const e = data.stats.enrollments;
        const steps = [['Por revisar', e.pending], ['Pago', e.payment], ['Aprobadas', e.approved]];
        const max = Math.max(1, ...steps.map(s => s[1]));
        const filter = this._admissionsFilter || 'pending';
        const tabs = [['pending', 'Por revisar', e.pending], ['approved', 'Aprobadas', e.approved], ['rejected', 'Rechazadas', e.rejected]];
        return `
            ${this._head('Solicitudes de admisión', { label: 'Ver todas', go: () => { if (typeof StudentView !== 'undefined') StudentView.currentSection = 'admissions'; App.navigate('alumnado/admissions'); } })}
            <div class="inv-funnel">
                ${steps.map(([label, n]) => `
                    <span class="inv-funnel-l">${label}</span>
                    <span class="inv-bar" aria-hidden="true"><i style="width:${Math.round(n / max * 100)}%"></i></span>
                    <strong class="inv-funnel-n">${n}</strong>`).join('')}
            </div>
            <div class="inv-tabs" role="group" aria-label="Filtrar solicitudes">
                ${tabs.map(([key, label, n]) => `<button type="button" class="inv-tab ${filter === key ? 'is-on' : ''}" aria-pressed="${filter === key}" ${UIEvents.attr('click', () => this.filterAdmissionsWidget(key))}>${label} · ${n}</button>`).join('')}
            </div>
            <div class="inv-list" id="inv-adm-list">${this._admissionsList(data)}</div>`;
    },

    filterAdmissionsWidget(status) {
        this._admissionsFilter = status;
        const card = document.getElementById('widget-inicio-admisiones');
        if (!card || !this._lastProposalData) return;
        card.querySelectorAll('.inv-tab').forEach((tab, i) => {
            const on = ['pending', 'approved', 'rejected'][i] === status;
            tab.classList.toggle('is-on', on);
            tab.setAttribute('aria-pressed', String(on));
        });
        const list = document.getElementById('inv-adm-list');
        if (list) SafeHTML.set(list, this._admissionsList(this._lastProposalData));
    },

    _wAcademico() {
        const now = new Date(), month = this._iso(now).slice(0, 7);
        const formations = typeof DataManager !== 'undefined' ? DataManager.getAll(DataManager.KEYS.FORMATIONS) : [];
        const allClasses = typeof ClassService !== 'undefined' ? ClassService.getClasses() : [];
        const teaching = new Set(allClasses.filter(c => String(c.startDate || '').startsWith(month)).map(c => c.formationId).filter(Boolean)).size;
        const rooms = [...new Set(this._classesOn(this._iso(now)).map(c => String(c.classroom || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es'));
        return `
            ${this._head('Académico', { label: 'Panel académico', go: () => App.navigate('academico') })}
            <div class="inv-tiles">
                <button type="button" class="inv-tile" ${UIEvents.attr('click', () => App.navigate('formaciones'))}>
                    <span class="inv-label">Formaciones</span>
                    <span class="inv-tile-value">${formations.length}</span>
                    <span class="inv-tile-sub">${teaching} con clases este mes</span>
                </button>
                <button type="button" class="inv-tile" id="inv-asistencia" ${UIEvents.attr('click', () => App.navigate('asistencia'))}>
                    <span class="inv-label">Lista pasada</span>
                    <span class="inv-tile-value">…</span>
                    <span class="inv-tile-sub">Últimos 3 meses</span>
                </button>
                <button type="button" class="inv-tile" id="inv-memorias" ${UIEvents.attr('click', () => App.navigate('memorias-asignatura'))}>
                    <span class="inv-label">Memorias</span>
                    <span class="inv-tile-value">…</span>
                    <span class="inv-tile-sub">Cargando…</span>
                </button>
                <button type="button" class="inv-tile" ${UIEvents.attr('click', () => App.navigate('calendario-formacion'))}>
                    <span class="inv-label">Aulas en uso hoy</span>
                    <span class="inv-tile-value">${rooms.length}</span>
                    <span class="inv-rooms">${rooms.length ? rooms.map(r => `<span class="inv-room">${Utils.sanitizeHTML(r)}</span>`).join('') : '<span class="inv-tile-sub">Sin clases hoy</span>'}</span>
                </button>
            </div>`;
    },

    // Cobertura (clases con lista pasada / clases que tocaban), agregada en el servidor sobre
    // toda la asistencia: el % de asistencia solo promedia las clases con lista y no
    // reflejaría que casi ninguna la tiene pasada.
    async _loadAttendance() {
        const tile = document.getElementById('inv-asistencia');
        if (!tile) return;
        try {
            const now = new Date(), cutoff = new Date(now.getFullYear(), now.getMonth() - 2, 1);
            const res = await fetch(`/api/attendance/summary?since=${this._iso(cutoff)}`, { headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() } });
            const data = await res.json();
            const target = document.getElementById('inv-asistencia');
            if (!target || !data.success) return;
            target.querySelector('.inv-tile-value').textContent = `${Math.round(data.coveragePct)} %`;
            target.querySelector('.inv-tile-sub').textContent = `${data.recordedOccurrences} de ${data.expectedOccurrences} clases · últimos 3 meses`;
        } catch (e) {
            console.error('Inicio: no se pudo cargar la asistencia', e);
        }
    },

    async _loadMemorias() {
        const tile = document.getElementById('inv-memorias');
        if (!tile) return;
        try {
            const res = await fetch('/api/subject-reports', { headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() } });
            const data = await res.json();
            const target = document.getElementById('inv-memorias');
            if (!target || !data.success || !data.reports) return;
            const total = typeof SubjectService !== 'undefined' ? SubjectService.getSubjects().length : 0;
            const done = Math.min(data.reports.length, total || data.reports.length);
            const missing = Math.max(0, total - done);
            target.classList.toggle('is-warn', missing > 0);
            target.querySelector('.inv-tile-value').textContent = `${done} / ${total}`;
            target.querySelector('.inv-tile-sub').textContent = missing ? `Faltan ${missing} por entregar` : 'Todas entregadas';
        } catch (e) {
            console.error('Inicio: no se pudieron cargar las memorias', e);
        }
    },

    _wContactos(data) {
        const leads = data.stats.leads;
        const recent = (data.recentLeads || []).slice(0, 3);
        return `
            ${this._head('Contactos', { label: 'Contactos', go: () => App.navigate('leads') })}
            <div class="inv-stats">
                ${this._stat(leads.pending, 'Sin contestar', 'is-warn')}
                ${this._stat(leads.contacted, 'Contactados')}
                ${this._stat(leads.visit || 0, 'Visita', 'is-ok')}
            </div>
            <div class="inv-list">
                ${recent.length ? recent.map(l => `
                    <button type="button" class="inv-line" ${UIEvents.attr('click', () => App.navigate('leads'))}>
                        <span><strong>${Utils.sanitizeHTML(l.nombre || 'Sin nombre')}</strong>${l.interes ? ` <span class="inv-muted">· ${Utils.sanitizeHTML(l.interes)}</span>` : ''}</span>
                        <span class="inv-muted">${this._timeAgo(l.createdAt)}</span>
                    </button>`).join('') : '<p class="inv-empty">Sin contactos recientes.</p>'}
            </div>`;
    },

    // Las nóminas de un mes pasan a pendientes el día 28 de ese mes (regla del centro,
    // 2026-10-25): hasta el 27 cuenta el mes anterior; desde el 28, también el mes en curso.
    _payrollDueMonths(now = new Date()) {
        const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const months = [{ month: prev.getMonth() + 1, year: prev.getFullYear() }];
        if (now.getDate() >= (typeof NominasUpload !== 'undefined' ? NominasUpload.DUE_DAY : 28)) months.push({ month: now.getMonth() + 1, year: now.getFullYear() });
        return months;
    },

    // Las clases de esos meses, para saber a quién le toca nómina; al llegar, se vuelve a pintar.
    _needPayrollMonths(onLoaded) {
        if (typeof FileStorage === 'undefined' || !FileStorage.needClasses) return;
        const months = this._payrollDueMonths();
        const first = months[0], last = months[months.length - 1];
        FileStorage.needClasses(new Date(first.year, first.month - 1, 1), new Date(last.year, last.month, 0), onLoaded);
    },

    // Nóminas que faltan de los meses que ya tocan, con la misma regla que Nóminas: clases
    // ese mes o un contrato que lo cubre. `latest` es el último de esos meses.
    _payrollPending() {
        if (typeof NominasUpload === 'undefined' || typeof FileStorage === 'undefined' || !FileStorage.read) return null;
        const history = FileStorage.read('ecpv_payroll_history') || [];
        const months = this._payrollDueMonths().map(({ month, year }) => {
            const uploaded = new Set(NominasUpload.payslipsOf(month, year, history).map(h => h.professorId));
            const due = [...NominasUpload.dueIds(month, year)].filter(id => DataManager.getProfessor(id));
            const missingIds = due.filter(id => !uploaded.has(id));
            return { month, year, monthName: NominasUpload.MONTH_NAMES[month - 1], since: this._iso(new Date(year, month - 1, 28)),
                uploaded: uploaded.size, expected: new Set([...due, ...uploaded]).size, missingIds,
                missing: missingIds.map(id => this._professorName(id)).sort((a, b) => a.localeCompare(b, 'es')) };
        });
        const late = months.filter(m => m.missingIds.length);
        return { months, latest: months[months.length - 1],
            rows: months.flatMap(m => m.missingIds.map(id => ({ id, monthName: m.monthName, since: m.since }))),
            label: (late.length ? late : [months[months.length - 1]]).map(m => m.monthName).join(' y ') };
    },

    _wDocencia() {
        const pending = this._payrollPending();
        const payroll = pending ? pending.latest : null;
        const earlier = pending ? pending.months.slice(0, -1).filter(m => m.missingIds.length) : [];
        const pct = payroll && payroll.expected ? Math.round(payroll.uploaded / payroll.expected * 100) : 100;
        const byTeacher = new Map();
        for (const c of this._classesOn(this._iso(new Date()))) {
            const id = this._classProfessorId(c);
            if (!id) continue;
            if (!byTeacher.has(id)) byTeacher.set(id, []);
            byTeacher.get(id).push(c.startTime);
        }
        const names = list => list.slice(0, 2).join(' y ') + (list.length > 2 ? ` y ${list.length - 2} más` : '');
        return `
            ${this._head('Docencia y nóminas', { label: 'Profesores', go: () => App.navigate('profesores') })}
            ${payroll ? `
                <button type="button" class="inv-ring-row" ${UIEvents.attr('click', () => App.navigate('profesores/nominas'))}>
                    <span class="inv-ring" style="--p:${pct}" aria-hidden="true"><span>${pct}%</span></span>
                    <span class="inv-person-text">
                        <strong>Nóminas de ${Utils.sanitizeHTML(payroll.monthName.toLowerCase())}${payroll.expected ? `: ${payroll.uploaded} de ${payroll.expected}` : ''}</strong>
                        <span>${!payroll.expected ? 'No tocaba subir ninguna' : payroll.missing.length ? `Faltan ${Utils.sanitizeHTML(names(payroll.missing))}` : 'Están todas subidas'}</span>
                        ${earlier.map(m => `<span class="inv-warn-text">Y de ${Utils.sanitizeHTML(m.monthName.toLowerCase())} faltan ${m.missingIds.length}</span>`).join('')}
                    </span>
                </button>` : ''}
            <h3 class="inv-label">Docentes con clase hoy</h3>
            <div class="inv-list">
                ${byTeacher.size ? [...byTeacher].map(([id, times]) => `
                    <button type="button" class="inv-line" ${UIEvents.attr('click', () => App.navigate('profesor/' + id))}>
                        <span>${Utils.sanitizeHTML(this._professorName(id) || 'Docente')}</span>
                        <span class="inv-muted">${Utils.sanitizeHTML(times.filter(Boolean).join(' y '))}</span>
                    </button>`).join('') : '<p class="inv-empty">Hoy nadie tiene clase.</p>'}
            </div>`;
    },

    _wLogistica(data) {
        const w = data.warehouse;
        const states = { pending: ['Por firmar', 'is-warn'], approved: ['Aprobada', 'is-ok'], rejected: ['Rechazada', 'is-bad'] };
        return `
            ${this._head('Logística y almacén', { label: 'Almacén', go: () => App.navigate('almacen') })}
            <div class="inv-stats">
                ${this._stat(w.pendingRequestsCount, 'Material por entregar')}
                ${this._stat(w.lowStockCount, 'Stock crítico', w.lowStockCount ? 'is-bad' : '')}
                ${this._stat(w.pendingOrdersCount, 'Por firmar', w.pendingOrdersCount ? 'is-warn' : '')}
            </div>
            <h3 class="inv-label">Órdenes de compra</h3>
            <div class="inv-list">
                ${(w.recentOrders || []).length ? w.recentOrders.map(o => {
                    const [label, tone] = states[o.status] || [o.status || '', ''];
                    return `
                        <button type="button" class="inv-line" ${UIEvents.attr('click', () => App.navigate('almacen'))}>
                            <span>${Utils.sanitizeHTML(o.reason || o.requesterName || 'Compra')} <span class="inv-muted">· ${this._money(o.totalCost)}</span></span>
                            <span class="inv-pill ${tone}">${Utils.sanitizeHTML(label)}</span>
                        </button>`;
                }).join('') : '<p class="inv-empty">No hay compras recientes.</p>'}
            </div>`;
    },

    // Como estaba (pedido del usuario): cinco alumnos activos al azar, con usuario,
    // formación, matrícula y estado; solo cambia el aspecto.
    _wAlumnado() {
        const allActive = (typeof DataManager !== 'undefined' ? DataManager.getAll(DataManager.KEYS.STUDENTS) : []).filter(s => s.active !== false);
        const shuffled = [...allActive];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        const students = shuffled.slice(0, 5);
        const formations = typeof DataManager !== 'undefined' ? DataManager.getAll(DataManager.KEYS.FORMATIONS) : [];
        const day = value => { const d = value ? new Date(value) : null; return d && !isNaN(d) ? d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'; };
        return `
            ${this._head('Alumnado registrado', { label: 'Ver todos', go: () => App.navigate('alumnado') })}
            ${students.length ? `
                <div class="inv-table-wrap">
                    <table class="inv-table">
                        <thead><tr><th scope="col">Alumno</th><th scope="col">Usuario</th><th scope="col">Formación</th><th scope="col">Matrícula</th><th scope="col">Estado</th></tr></thead>
                        <tbody>
                            ${students.map(s => {
                                const fullName = `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Alumno';
                                const account = AuthManager.getStudentUser ? AuthManager.getStudentUser(s.id) : null;
                                const formationNames = (s.formations || []).map(id => formations.find(f => f.id === id)).filter(Boolean).map(f => f.name).join(', ');
                                return `
                                    <tr class="inv-row-link" ${UIEvents.attr('click', () => App.navigate(Utils.studentProfileUrl(s)))}>
                                        <td>
                                            <div class="inv-stu">
                                                <span class="inv-avatar" aria-hidden="true">${Utils.sanitizeHTML(this._initials(fullName))}</span>
                                                <span class="inv-person-text">
                                                    <a href="/${Utils.sanitizeHTML(Utils.studentProfileUrl(s))}" ${UIEvents.attr('click', event => { event.preventDefault(); event.stopPropagation(); App.navigate(Utils.studentProfileUrl(s)); })}>${Utils.sanitizeHTML(fullName)}</a>
                                                    ${s.email ? `<span>${Utils.sanitizeHTML(s.email)}</span>` : ''}
                                                </span>
                                            </div>
                                        </td>
                                        <td class="inv-user">${account ? Utils.sanitizeHTML(account.username) : '<span class="inv-muted">Sin acceso</span>'}</td>
                                        <td>${formationNames ? Utils.sanitizeHTML(formationNames) : '<span class="inv-muted">Sin formación</span>'}</td>
                                        <td class="inv-mono">${day(s.enrollmentDate)}</td>
                                        <td><span class="inv-pill is-ok"><i class="inv-pill-dot" aria-hidden="true"></i>Activo</span></td>
                                    </tr>`;
                            }).join('')}
                        </tbody>
                    </table>
                </div>` : '<p class="inv-empty">Todavía no hay alumnos registrados.</p>'}`;
    },

    // Como estaba (pedido del usuario): una columna por día con sus clases (hora y
    // asignatura) que llevan a su programación; sábado y domingo solo si hay clases.
    _wSemana() {
        const monday = this._monday(), today = this._iso(new Date());
        const holidays = typeof DataManager !== 'undefined' ? (DataManager.getHolidays() || []) : [];
        const labels = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];
        const palette = ['is-c1', 'is-c2', 'is-c3', 'is-c4', 'is-c5'];
        const colorOf = new Map();
        const days = labels.map((label, i) => {
            const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i), iso = this._iso(d);
            return { label: `${label} ${d.getDate()}`, iso, classes: this._classesOn(iso), weekend: i >= 5 };
        }).filter(d => !d.weekend || d.classes.length);
        return `
            ${this._head('Calendario · esta semana', { label: 'Ver semana completa', go: () => App.navigate('calendario') })}
            <div class="inv-week-cols" style="grid-template-columns: repeat(${days.length}, minmax(150px, 1fr));">
                ${days.map(d => `
                    <div class="inv-week-col ${d.iso === today ? 'is-today' : ''} ${holidays.includes(d.iso) ? 'is-holiday' : ''}">
                        <div class="inv-week-head">
                            <span>${d.label}</span>
                            ${d.iso === today ? '<span class="inv-pill is-ok">Hoy</span>' : holidays.includes(d.iso) ? '<span class="inv-pill is-bad">Festivo</span>' : `<span class="inv-muted">${d.classes.length || ''}</span>`}
                        </div>
                        <div class="inv-week-body">
                            ${d.classes.map(c => {
                                const subject = typeof SubjectService !== 'undefined' ? SubjectService.getSubject(c.subjectId) : null;
                                const formation = typeof FormationService !== 'undefined' ? FormationService.getFormation(c.formationId) : null;
                                const key = c.formationId || c.subjectId || '';
                                if (!colorOf.has(key)) colorOf.set(key, palette[colorOf.size % palette.length]);
                                const subjectName = subject ? subject.name : 'Clase', formationName = formation ? formation.name : '';
                                return `
                                    <button type="button" class="inv-ev ${colorOf.get(key)}" title="Ir a la programación de esta clase"
                                        ${UIEvents.attr('click', () => CategoryDashboards.goToClassSchedule(c.formationId || '', c.subjectId || '', formationName, subjectName))}>
                                        <span class="inv-ev-time">${Utils.sanitizeHTML(c.startTime || '')}${c.endTime ? `–${Utils.sanitizeHTML(c.endTime)}` : ''}</span>
                                        <strong>${Utils.sanitizeHTML(subjectName)}</strong>
                                        ${formationName || c.classroom ? `<span class="inv-ev-sub">${Utils.sanitizeHTML([formationName, c.classroom].filter(Boolean).join(' · '))}</span>` : ''}
                                    </button>`;
                            }).join('') || '<span class="inv-week-empty">Sin clases</span>'}
                        </div>
                    </div>`).join('')}
            </div>`;
    },

    _wFestivos() {
        const today = this._iso(new Date());
        const upcoming = this.getUpcomingHolidays();
        return `
            ${this._head('Próximos festivos', this._can('settings') ? { label: 'Calendario escolar', go: () => App.navigate('settings') } : null)}
            <div class="inv-list">
                ${upcoming.length ? upcoming.map(h => {
                    const d = new Date(`${h}T12:00:00`);
                    const daysLeft = Math.round((d - new Date(`${today}T12:00:00`)) / 86400000);
                    const weekday = d.toLocaleDateString('es-ES', { weekday: 'long' });
                    return `
                        <div class="inv-holiday">
                            <span class="inv-holiday-date">${d.getDate()} ${d.toLocaleDateString('es-ES', { month: 'short' }).replace('.', '').toUpperCase()}</span>
                            <span>${weekday.charAt(0).toUpperCase() + weekday.slice(1)} · día no lectivo</span>
                            <span class="inv-muted">${daysLeft === 0 ? 'hoy' : daysLeft === 1 ? 'mañana' : `en ${daysLeft} días`}</span>
                        </div>`;
                }).join('') : '<p class="inv-empty">No hay festivos marcados próximamente.</p>'}
            </div>`;
    },

    _messageRows() {
        const messages = this.getRecentMessages().slice(0, 4);
        if (!messages.length) return '<p class="inv-empty">No tienes mensajes sin leer.</p>';
        return messages.map(m => `
            <button type="button" class="inv-person" ${UIEvents.attr('click', () => { MessagesView.selectMessage(m.id); App.navigate('mensajes'); })}>
                <span class="inv-avatar" aria-hidden="true">${Utils.sanitizeHTML(this._initials(m.senderName))}</span>
                <span class="inv-person-text"><strong>${Utils.sanitizeHTML(m.senderName || '')} <span class="inv-muted">· ${this._timeAgo(m.date)}</span></strong><span>${Utils.sanitizeHTML(m.subject || '')}</span></span>
            </button>`).join('');
    },

    _wMensajes() {
        return `
            ${this._head('Mensajes recientes', { label: 'Buzón', go: () => App.navigate('mensajes') })}
            <div class="inv-list" id="inv-msg-list">${this._messageRows()}</div>`;
    },

    _wAccesos() {
        const links = [['Profesorado', 'profesores'], ['Alumnado', 'alumnado'], ['Contactos', 'leads'], ['Pre-Matrícula', 'pre-matricula'], ['Programación', 'calendario-formacion'], ['Calendario escolar', 'settings']];
        return `
            ${this._head('Accesos rápidos')}
            <nav class="inv-quick" aria-label="Accesos rápidos">
                ${links.map(([label, route]) => `<a href="/${route}" class="inv-quick-link" ${UIEvents.attr('click', event => { event.preventDefault(); App.navigate(route); })}>${label}</a>`).join('')}
            </nav>`;
    },

    _dnrWidgetShell(w, isEditMode, contentHtml, i, dashboardId = 'dashboard') {
        const span = w.span || 1;
        const controls = CategoryDashboards.renderWidgetHeaderControls(dashboardId, w.id, w.name, span, isEditMode);
        return `
            <section id="widget-${Utils.sanitizeHTML(w.id)}" data-widget-id="${Utils.sanitizeHTML(w.id)}" draggable="${isEditMode ? 'true' : 'false'}"
                 class="dnr-card dashboard-widget-card inv-card inv-${Utils.sanitizeHTML(w.id)} ${span >= 2 ? 'is-wide' : ''} ${isEditMode ? 'edit-mode' : ''}"
                 style="grid-column: span ${span}; display: ${w.visible ? 'flex' : 'none'}; --dnr-i:${i || 0}; --dnr-tier:${span >= 2 ? 1 : 0};
                        cursor:${isEditMode ? 'grab' : 'default'}; ${isEditMode ? 'border:2px dashed var(--dnr-teal-500);' : ''}">
                ${controls}
                <div class="inv-card-body" ${isEditMode ? 'style="padding-top: 2.4rem;"' : ''}>${contentHtml}</div>
            </section>`;
    },

    openStudentFromEnrollment(enrollmentId, explicitStudentId) {
        // URL amigable con nombre — si el alumno ya está en la caché local se
        // construye con su nombre real; si no (aún no ha sincronizado), se cae
        // al id a secas, que App.navigate sigue aceptando igual (ver el router
        // de 'alumno' en app.js).
        const urlFor = (id) => {
            const student = (typeof StudentService !== 'undefined') ? StudentService.getStudent(id) : null;
            return student ? Utils.studentProfileUrl(student) : `alumno/${id}`;
        };

        if (explicitStudentId) {
            App.navigate(urlFor(explicitStudentId));
            return;
        }

        const all = (this._stats && this._stats.recentEnrollments) ? this._stats.recentEnrollments : [];
        const en = all.find(item => item.id === enrollmentId);
        if (en && en.studentId) {
            App.navigate(urlFor(en.studentId));
            return;
        }

        // Search in StudentService as fallback
        if (typeof StudentService !== 'undefined' && StudentService.getStudents) {
            const students = StudentService.getStudents();
            const norm = s => (s || '').trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
            const enDni = en ? norm(en.dni) : '';
            const enFirst = en ? norm(en.nombre) : '';
            const enLast = en ? norm(en.apellidos) : '';

            let m = null;
            if (enDni && enDni.length >= 3) m = students.find(s => norm(s.dni) === enDni);
            if (!m && enFirst && enLast) m = students.find(s => norm(s.firstName) === enFirst && norm(s.lastName) === enLast);
            if (!m && enFirst) m = students.find(s => norm(s.firstName) === enFirst);
            if (m) {
                App.navigate(Utils.studentProfileUrl(m));
                return;
            }
        }

        // Fallback to registered students tab
        if (typeof StudentView !== 'undefined') { StudentView.currentSection = 'registered'; }
        App.navigate('alumnado');
    },

    getRecentMessages() {
        if (typeof AuthManager === 'undefined' || typeof DataManager === 'undefined') return [];
        const currentUser = AuthManager.getCurrentUser();
        if (!currentUser) return [];
        const allMessages = DataManager.getMessages ? DataManager.getMessages() : (DataManager.getAll(DataManager.KEYS.MESSAGES) || []);
        if (!Array.isArray(allMessages)) return [];

        const isTrashed = (m, u) => (Array.isArray(m.trashFor) && m.trashFor.includes(u)) || (!m.trashFor && m.trash);
        const isDeleted = (m, u) => Array.isArray(m.deletedFor) && m.deletedFor.includes(u);

        // Solo mensajes no leídos dirigidos al usuario actual, que no estén en papelera ni eliminados
        const unreadMessages = allMessages.filter(m =>
            m.receiverId === currentUser.username &&
            !m.read &&
            !isTrashed(m, currentUser.username) &&
            !isDeleted(m, currentUser.username)
        );

        // El de arriba del todo tiene que ser el último recibido (orden descendente por fecha)
        unreadMessages.sort((a, b) => new Date(b.date) - new Date(a.date));

        return unreadMessages;
    },

    updateMessagesWidget() {
        const list = document.getElementById('inv-msg-list');
        if (list) SafeHTML.set(list, this._messageRows());
    },

    getUpcomingHolidays() {
        if (typeof DataManager === 'undefined') return [];
        const holidays = DataManager.getHolidays() || [];
        const today = this._iso(new Date());
        return holidays.filter(h => h >= today).sort().slice(0, 3);
    },

    async showPendingActionsModal() {
        if (typeof ModalManager === 'undefined') {
            console.error('ModalManager is not loaded');
            alert('El gestor de modales no está disponible.');
            return;
        }

        // Mostrar un estado de carga inicial en el modal
        ModalManager.show(
            'ACCIONES PENDIENTES HOY',
            `<div style="text-align: center; padding: 3rem 2rem;">
                <i class="fas fa-spinner fa-spin" style="font-size: 2rem; color: #0284c7; margin-bottom: 1rem;"></i>
                <div style="font-weight: 700; color: #1e293b;">Cargando tareas pendientes...</div>
            </div>`
        );

        try {
            const token = AuthManager.getCsrfToken();
            const headers = { 'X-SICAF-CSRF': token };

            // Realizar las llamadas en paralelo
            const [statsRes, reqsRes, ordersRes, leadsRes, inventoryRes] = await Promise.all([
                fetch('/api/dashboard/stats', { headers }).then(r => r.json()),
                fetch('/data/warehouse-requests.json', { headers }).then(r => r.json()),
                fetch('/data/purchase-orders.json', { headers }).then(r => r.json()),
                // Only pending contacts (the newest 200), not the whole list.
                fetch('/api/leads?status=pending&limit=200', { headers }).then(r => r.json()).then(r => r.leads || []).catch(() => []),
                // Names for the requested items; without inventory access the ids are shown.
                fetch('/data/inventory.json', { headers }).then(r => r.json()).catch(() => [])
            ]);
            const inventoryNames = new Map((Array.isArray(inventoryRes) ? inventoryRes : []).map(item => [item.id, item.name]));
            const itemName = i => i.name || inventoryNames.get(i.itemId) || i.itemId;

            const recentEnrollments = statsRes.recentEnrollments || [];
            const pendingRequests = Array.isArray(reqsRes) ? reqsRes.filter(r => r.status === 'pending') : [];
            const pendingOrders = Array.isArray(ordersRes) ? ordersRes.filter(o => o.status === 'pending') : [];
            const pendingLeads = Array.isArray(leadsRes) ? leadsRes.filter(l => l.status === 'pending') : [];

            // Si no hay absolutamente nada pendiente
            if (recentEnrollments.length === 0 && pendingRequests.length === 0 && pendingOrders.length === 0 && pendingLeads.length === 0) {
                ModalManager.show(
                    'ACCIONES PENDIENTES HOY',
                    `<div style="text-align: center; padding: 4rem 2rem;">
                        <div style="width: 64px; height: 64px; border-radius: 50%; background: #f0fdf4; border: 1px solid #bbf7d0; display: inline-flex; align-items: center; justify-content: center; color: #16a34a; font-size: 1.5rem; margin-bottom: 1.25rem;">
                            <i class="fas fa-check-double"></i>
                        </div>
                        <h3 style="font-family: inherit; font-size: 1.25rem; font-weight: 800; color: #0f172a; margin-bottom: 0.5rem;">¡Estás al día!</h3>
                        <p style="color: #64748b; font-size: 0.9rem; margin: 0;">No tienes ninguna tarea o aprobación pendiente de revisión para hoy.</p>
                    </div>`
                );
                return;
            }

            // Construir contenido HTML
            let contentHTML = `
                <style ${SafeHTML.styleAttr}>
                    .dnr-modal-scroll {
                        max-height: 70vh;
                        overflow-y: auto;
                        padding-right: 0.5rem;
                    }
                    .dnr-modal-scroll::-webkit-scrollbar {
                        width: 6px;
                    }
                    .dnr-modal-scroll::-webkit-scrollbar-track {
                        background: #f1f5f9;
                        border-radius: 4px;
                    }
                    .dnr-modal-scroll::-webkit-scrollbar-thumb {
                        background: #cbd5e1;
                        border-radius: 4px;
                    }
                    .dnr-modal-scroll::-webkit-scrollbar-thumb:hover {
                        background: #94a3b8;
                    }
                    .dnr-modal-section-title {
                        font-family: 'Bebas Neue', sans-serif;
                        font-size: 1.35rem;
                        letter-spacing: 0.04em;
                        color: #0f172a;
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        margin: 1.5rem 0 0.75rem 0;
                        border-bottom: 2px solid #e2e8f0;
                        padding-bottom: 0.4rem;
                    }
                    .dnr-modal-section-title:first-child {
                        margin-top: 0;
                    }
                    .dnr-modal-item {
                        background: #ffffff;
                        border: 1px solid #e2e8f0;
                        border-radius: 10px;
                        padding: 0.75rem 1rem;
                        margin-bottom: 0.5rem;
                        transition: all 0.2s ease;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 1rem;
                        box-shadow: 0 1px 2px rgba(0,0,0,0.02);
                    }
                    .dnr-modal-item:hover {
                        background: #f0f9ff;
                        border-color: #bae6fd;
                        transform: translateX(4px);
                        box-shadow: 0 3px 8px rgba(2, 132, 199, 0.05);
                    }
                    .dnr-modal-badge {
                        padding: 0.2rem 0.5rem;
                        border-radius: 6px;
                        font-size: 0.7rem;
                        font-weight: 700;
                        text-transform: uppercase;
                    }
                    .dnr-modal-badge-amber {
                        background: #fef3c7;
                        color: #d97706;
                        border: 1px solid #fde68a;
                    }
                    .dnr-modal-badge-blue {
                        background: #e0f2fe;
                        color: #0284c7;
                        border: 1px solid #bae6fd;
                    }
                    .dnr-modal-badge-teal {
                        background: #ccfbf1;
                        color: #0f766e;
                        border: 1px solid #99f6e4;
                    }
                    .dnr-modal-manage-btn {
                        background: #0284c7;
                        color: white;
                        border: none;
                        border-radius: 6px;
                        padding: 0.4rem 0.75rem;
                        font-size: 0.75rem;
                        font-weight: 700;
                        cursor: pointer;
                        transition: background 0.15s ease;
                    }
                    .dnr-modal-manage-btn:hover {
                        background: #0369a1;
                    }
                </style>
                <div class="dnr-proposal dnr-modal-scroll">
            `;

            // 1. ADMISIONES (PRE-MATRÍCULAS)
            if (recentEnrollments.length > 0) {
                contentHTML += `
                    <div class="dnr-modal-section-title">
                        <i class="fas fa-file-signature" style="color: #0284c7;"></i> Solicitudes de Admisión (${recentEnrollments.length})
                    </div>
                    <div>
                `;
                recentEnrollments.forEach(en => {
                    const dateStr = new Date(en.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
                    const statusClass = en.status === 'payment' ? 'dnr-modal-badge-teal' : 'dnr-modal-badge-amber';
                    const statusText = en.status === 'payment' ? 'Pendiente Pago' : 'Pendiente Revisión';
                    contentHTML += `
                        <div class="dnr-modal-item">
                            <div style="min-width: 0;">
                                <div style="font-weight: 700; color: #0f172a; font-size: 0.88rem;">${Utils.sanitizeHTML(en.nombre)} ${Utils.sanitizeHTML(en.apellidos)}</div>
                                <div style="color: #64748b; font-size: 0.75rem; font-weight: 500; margin-top: 1px;">
                                    ${Utils.sanitizeHTML(en.email)} · <span style="text-transform: capitalize;">${en.tipoFormacion}</span>
                                </div>
                            </div>
                            <div style="display: flex; align-items: center; gap: 0.75rem; flex-shrink: 0;">
                                <span class="dnr-modal-badge ${statusClass}">${statusText}</span>
                                <span style="font-size: 0.72rem; color: #94a3b8; font-weight: 600;">${dateStr}</span>
                                <button class="dnr-modal-manage-btn" ${UIEvents.attr("click", function(event,captured){ModalManager.close();if(typeof StudentView!=='undefined'){StudentView.currentSection='admissions';}App.navigate('alumnado');}, [])}>Gestionar</button>
                            </div>
                        </div>
                    `;
                });
                contentHTML += `</div>`;
            }

            // 2. LEADS PENDIENTES
            if (pendingLeads.length > 0) {
                contentHTML += `
                    <div class="dnr-modal-section-title">
                        <i class="fas fa-filter" style="color: #6366f1;"></i> Contactos Pendientes (Leads: ${pendingLeads.length})
                    </div>
                    <div>
                `;
                pendingLeads.slice(0, 5).forEach(lead => {
                    const dateStr = new Date(lead.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
                    contentHTML += `
                        <div class="dnr-modal-item">
                            <div style="min-width: 0;">
                                <div style="font-weight: 700; color: #0f172a; font-size: 0.88rem;">${Utils.sanitizeHTML(lead.nombre)}</div>
                                <div style="color: #64748b; font-size: 0.75rem; font-weight: 500; margin-top: 1px;">
                                    ${Utils.sanitizeHTML(lead.email)} · Interés: <span style="font-weight: 600; color: #475569;">${Utils.sanitizeHTML(lead.interes || 'Sin especificar')}</span>
                                </div>
                            </div>
                            <div style="display: flex; align-items: center; gap: 0.75rem; flex-shrink: 0;">
                                <span class="dnr-modal-badge dnr-modal-badge-blue">Nuevo Lead</span>
                                <span style="font-size: 0.72rem; color: #94a3b8; font-weight: 600;">${dateStr}</span>
                                <button class="dnr-modal-manage-btn" ${UIEvents.attr("click", function(event,captured){ModalManager.close();App.navigate('leads');}, [])}>Contactar</button>
                            </div>
                        </div>
                    `;
                });
                if (pendingLeads.length > 5) {
                    contentHTML += `
                        <div style="text-align: center; margin-top: 0.5rem;">
                            <a href="#" ${UIEvents.attr("click", function(event,captured){event.preventDefault();ModalManager.close();App.navigate('leads');}, [])} style="font-size: 0.78rem; font-weight: 700; color: #0284c7; text-decoration: none;">Ver los ${pendingLeads.length - 5} contactos pendientes más →</a>
                        </div>
                    `;
                }
                contentHTML += `</div>`;
            }

            // 3. PETICIONES DE MATERIAL (LOGÍSTICA)
            if (pendingRequests.length > 0) {
                contentHTML += `
                    <div class="dnr-modal-section-title">
                        <i class="fas fa-boxes" style="color: #10b981;"></i> Solicitudes de Material Pendientes (${pendingRequests.length})
                    </div>
                    <div>
                `;
                pendingRequests.forEach(req => {
                    const dateStr = new Date(req.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
                    let itemsArray = [];
                    try {
                        itemsArray = typeof req.items === 'string' ? JSON.parse(req.items) : req.items;
                    } catch(e) {}
                    const itemsText = Array.isArray(itemsArray) ? itemsArray.map(i => `${i.quantity}x ${itemName(i)}`).join(', ') : 'Varios artículos';

                    contentHTML += `
                        <div class="dnr-modal-item">
                            <div style="min-width: 0;">
                                <div style="font-weight: 700; color: #0f172a; font-size: 0.88rem;">${Utils.sanitizeHTML(req.studentName)}</div>
                                <div style="color: #64748b; font-size: 0.75rem; font-weight: 500; margin-top: 1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                                    ${Utils.sanitizeHTML(itemsText)}
                                </div>
                            </div>
                            <div style="display: flex; align-items: center; gap: 0.75rem; flex-shrink: 0;">
                                <span class="dnr-modal-badge dnr-modal-badge-amber">Por Entregar</span>
                                <span style="font-size: 0.72rem; color: #94a3b8; font-weight: 600;">${dateStr}</span>
                                <button class="dnr-modal-manage-btn" ${UIEvents.attr("click", function(event,captured){ModalManager.close();App.navigate('almacen');}, [])}>Entregar</button>
                            </div>
                        </div>
                    `;
                });
                contentHTML += `</div>`;
            }

            // 4. COMPRAS PENDIENTES DE FIRMA
            if (pendingOrders.length > 0) {
                contentHTML += `
                    <div class="dnr-modal-section-title">
                        <i class="fas fa-file-invoice-dollar" style="color: #f59e0b;"></i> Órdenes de Compra Pendientes de Firma (${pendingOrders.length})
                    </div>
                    <div>
                `;
                pendingOrders.forEach(o => {
                    const dateStr = new Date(o.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
                    let itemsArray = [];
                    try {
                        itemsArray = typeof o.items === 'string' ? JSON.parse(o.items) : o.items;
                    } catch(e) {}
                    const itemsCount = Array.isArray(itemsArray) ? itemsArray.length : 0;

                    contentHTML += `
                        <div class="dnr-modal-item">
                            <div style="min-width: 0;">
                                <div style="font-weight: 700; color: #0f172a; font-size: 0.88rem;">Solicitado por: ${Utils.sanitizeHTML(o.requesterName)}</div>
                                <div style="color: #64748b; font-size: 0.75rem; font-weight: 500; margin-top: 1px;">
                                    Motivo: ${Utils.sanitizeHTML(o.reason || 'No especificado')} · <span style="font-weight: 700; color: #1e293b;">${itemsCount} artículos</span>
                                </div>
                            </div>
                            <div style="display: flex; align-items: center; gap: 0.75rem; flex-shrink: 0;">
                                <span style="font-weight: 800; color: #b91c1c; font-size: 0.85rem; padding-right: 0.25rem;">${o.totalCost.toLocaleString('es-ES')}€</span>
                                <span class="dnr-modal-badge dnr-modal-badge-amber">Firma</span>
                                <button class="dnr-modal-manage-btn" ${UIEvents.attr("click", function(event,captured){ModalManager.close();App.navigate('almacen');}, [])}>Revisar</button>
                            </div>
                        </div>
                    `;
                });
                contentHTML += `</div>`;
            }

            contentHTML += `</div>`;

            ModalManager.show('ACCIONES PENDIENTES HOY', contentHTML);

        } catch (err) {
            console.error('Error al cargar detalles de acciones pendientes:', err);
            ModalManager.show(
                'ACCIONES PENDIENTES HOY',
                `<div style="text-align: center; padding: 3rem 2rem;">
                    <i class="fas fa-triangle-exclamation" style="font-size: 2rem; color: #ef4444; margin-bottom: 1rem;"></i>
                    <div style="font-weight: 700; color: #1e293b;">Error al cargar las acciones pendientes</div>
                    <div style="font-size: 0.85rem; color: #64748b; margin-top: 0.5rem;">Por favor, inténtalo de nuevo o revisa la consola del sistema.</div>
                </div>`
            );
        }
    },

    async openFinancialConfigModal() {
        if (typeof ModalManager === 'undefined') return;
        ModalManager.show(
            'CONFIGURACIÓN DE TARIFAS Y COSTES',
            `<div class="dnr-proposal financial-form-dnr" style="text-align: center; padding: 2.5rem 1rem;">
                <i class="fas fa-spinner fa-spin" style="font-size: 1.8rem; color: var(--dnr-teal-600); margin-bottom: 0.75rem;"></i>
                <div style="font-weight: 700; color: var(--dnr-ink);">Cargando tarifas oficiales...</div>
            </div>`
        );

        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/settings/financial', {
                headers: { 'X-SICAF-CSRF': token }
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Error al cargar configuración');

            // Tarifa por TIPO guardada antes (tuitionPrices): solo como valor de partida para
            // formaciones sin precio propio. Sin ella, el precio empieza en 0.
            const typeDefaults = data.config.tuitionPrices || {};
            const formationPrices = data.config.formationPrices || {};

            // Cada formación tiene su propio precio (dos másteres, p.ej. Vestuario
            // y Producción, pueden costar distinto aunque compartan tipo) — antes
            // este formulario solo permitía fijar un precio por tipo genérico.
            const formations = DataManager.getFormations();
            // Order, icon and plural of each type: Plan de Estudios → Tipos.
            const typeOrder = FormationTypeService.getTypes().map(t => t.id);
            const typeIcon = type => 'fas ' + FormationTypeService.icon(type).replace(/[^a-z0-9-]/g, '');
            const pluralLabel = type => Utils.sanitizeHTML(FormationTypeService.plural(type));

            const groups = new Map();
            formations.forEach(f => {
                const type = f.type || '';
                if (!groups.has(type)) groups.set(type, []);
                groups.get(type).push(f);
            });
            const sortedTypes = [...groups.keys()].sort((a, b) => {
                const idxA = typeOrder.indexOf(a);
                const idxB = typeOrder.indexOf(b);
                return (idxA > -1 ? idxA : 99) - (idxB > -1 ? idxB : 99);
            });

            const content = `
                <div class="dnr-proposal financial-form-dnr">
                    <p class="ffd-intro">
                        Ajusta la tarifa oficial de cada formación por separado — dos formaciones del mismo tipo (p.ej. dos másteres) pueden tener precios distintos. Estas tarifas se utilizan para valorar las matrículas y proyectar los ingresos y márgenes del centro en tiempo real.
                    </p>
                    <form id="form-financial-config" ${UIEvents.attr("submit", function(event,captured){event.preventDefault();DashboardView.saveFinancialConfig();}, [])}>
                        <div class="ffd-formation-list">
                            ${sortedTypes.map(type => `
                                <div class="ffd-group">
                                    <h4 class="ffd-group-title"><i class="${typeIcon(type)}"></i> ${pluralLabel(type)}</h4>
                                    ${groups.get(type).map(f => {
                                        const savedPrice = formationPrices[f.id];
                                        const value = Number(savedPrice !== undefined ? savedPrice : typeDefaults[type]) || 0;
                                        return `
                                            <div class="ffd-formation-row">
                                                <div class="ffd-formation-name">
                                                    ${Utils.sanitizeHTML(f.name)}
                                                    ${f.commercialName ? `<span class="ffd-formation-sub">${Utils.sanitizeHTML(f.commercialName)}</span>` : ''}
                                                </div>
                                                <div class="ffd-formation-price">
                                                    <input type="number" class="form-input ffd-price-input" data-formation-id="${Utils.sanitizeHTML(f.id)}" value="${value}" required min="0" step="50">
                                                    <span class="ffd-currency">€</span>
                                                </div>
                                            </div>
                                        `;
                                    }).join('')}
                                </div>
                            `).join('')}
                        </div>
                    </form>
                </div>
            `;

            ModalManager.show(
                'CONFIGURACIÓN DE TARIFAS Y COSTES',
                content,
                [
                    {
                        text: 'Cancelar',
                        class: 'btn-secondary',
                        action: () => ModalManager.close()
                    },
                    {
                        text: '<i class="fas fa-floppy-disk"></i> Guardar Tarifas',
                        class: 'btn-primary',
                        action: () => DashboardView.saveFinancialConfig()
                    }
                ]
            );
        } catch (err) {
            console.error('Error cargando tarifas:', err);
            ModalManager.show(
                'ERROR',
                `<div class="dnr-proposal financial-form-dnr" style="text-align:center; padding:2rem 1rem; color:var(--dnr-danger);">
                    <i class="fas fa-circle-exclamation" style="font-size:2rem; margin-bottom:0.75rem;"></i>
                    <p>No se pudo cargar la configuración de tarifas.</p>
                </div>`
            );
        }
    },

    async saveFinancialConfig() {
        const formationPrices = {};
        document.querySelectorAll('.ffd-price-input').forEach(input => {
            formationPrices[input.dataset.formationId] = parseFloat(input.value) || 0;
        });

        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/settings/financial', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-SICAF-CSRF': token
                },
                body: JSON.stringify({ formationPrices })
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Error al guardar tarifas');

            ModalManager.close();
            if (typeof Utils !== 'undefined' && Utils.showToast) {
                Utils.showToast('Tarifas actualizadas correctamente', 'success');
            }
            // Recargar datos y refrescar panel
            this._loadAndRender();
        } catch (err) {
            console.error('Error guardando tarifas:', err);
            if (typeof Utils !== 'undefined' && Utils.showToast) {
                Utils.showToast(err.message || 'Error al guardar tarifas', 'error');
            }
        }
    },

    attachEventListeners() {}
};

window.DashboardView = DashboardView;

// Acciones pendientes, diseño B (2026-10-25): arriba lo que tiene fecha (altas de
// profesorado), después una tarjeta por tipo con lo que más espera primero y su
// botón de acción. Estilos en assets/css/views/acciones-pendientes.css.
const AccionesPendientesView = {
    _cache: null,
    searchQuery: '',
    _expanded: {},
    VISIBLE: 4,

    render() {
        setTimeout(() => this._loadAndRender(), 0);
        return `
            <div id="acciones-pendientes-content" class="view-container dnr-proposal ap-view" style="padding-top: 1rem;">
                <div class="dnr-skel dnr-skel-hero"></div>
                <div style="margin-top: 2rem;">
                    <div class="dnr-skel dnr-skel-card" style="height: 100px; margin-bottom: 1rem;"></div>
                    <div class="dnr-skel dnr-skel-card" style="height: 100px; margin-bottom: 1rem;"></div>
                </div>
            </div>
        `;
    },

    async _loadAndRender() {
        const contentArea = document.getElementById('acciones-pendientes-content');
        if (!contentArea) return;

        try {
            const token = AuthManager.getCsrfToken();
            const headers = { 'X-SICAF-CSRF': token };

            const [statsRes, reqsRes, ordersRes, leadsRes, inventoryRes] = await Promise.all([
                fetch('/api/dashboard/stats', { headers }).then(r => r.json()).catch(() => ({})),
                fetch('/data/warehouse-requests.json', { headers }).then(r => r.json()).catch(() => []),
                fetch('/data/purchase-orders.json', { headers }).then(r => r.json()).catch(() => []),
                fetch('/api/leads?status=pending&limit=200', { headers }).then(r => r.json()).catch(() => []),
                fetch('/data/inventory.json', { headers }).then(r => r.json()).catch(() => [])
            ]);
            // Requests store only the item id: show the inventory name (the id if it is not available).
            this._inventoryNames = new Map((Array.isArray(inventoryRes) ? inventoryRes : []).map(item => [item.id, item.name]));

            // Solo lo que de verdad espera trámite (ni aprobadas ni rechazadas).
            const rawEnrollments = statsRes.recentEnrollments || [];
            const pendingEnrollments = rawEnrollments.filter(en => ['pending', 'interview', 'payment'].includes(en.status));
            const pendingRequests = Array.isArray(reqsRes) ? reqsRes.filter(r => r.status === 'pending') : [];
            const pendingOrders = Array.isArray(ordersRes) ? ordersRes.filter(o => o.status === 'pending') : [];
            const allLeads = Array.isArray(leadsRes) ? leadsRes : (leadsRes.leads || []);
            const pendingLeads = allLeads.filter(l => l.status === 'pending');

            // Profesores cuya fecha de alta cae dentro de los próximos 10 días
            // — mismo umbral que el aviso diario a asesoría en
            // ProfessorService.checkDailyAdvisoryNotification, para no tener
            // dos definiciones distintas de "próxima" en la app.
            const allProfessors = (typeof DataManager !== 'undefined' && DataManager.getProfessors) ? (DataManager.getProfessors() || []) : [];
            const upcomingHires = allProfessors.filter(p => {
                if (!p.hireDate) return false;
                const days = this._daysUntil(p.hireDate);
                return days >= 0 && days <= 10;
            });

            // Lo mismo que cuenta «Pendiente hoy» en el Inicio: nóminas del mes pasado que
            // faltan y justificantes sin abrir, cada uno solo para quien puede verlo.
            const payroll = DashboardView._can('nominas') ? DashboardView._payrollPending() : null;
            if (payroll) DashboardView._needPayrollMonths(() => {
                if (App.currentView !== 'acciones-pendientes' || !this._cache) return;
                this._cache.payroll = DashboardView._payrollPending();
                this._renderShell();
            });
            const reviewer = typeof JustificantesView !== 'undefined' && JustificantesView.isReviewer();
            const freshDocs = reviewer ? JustificantesView.all().filter(d => !d.reviewedAt) : [];

            this._cache = { pendingEnrollments, pendingLeads, pendingRequests, pendingOrders, upcomingHires, payroll, reviewer, freshDocs };
            this.searchQuery = '';
            this._expanded = {};
            this._renderShell();

        } catch (err) {
            console.error('Error al cargar la bandeja de acciones pendientes:', err);
            SafeHTML.set(contentArea, `
                <div class="ap-card ap-empty-state">
                    <h2>No se pudieron cargar las acciones pendientes</h2>
                    <p>Comprueba la conexión con el servidor.</p>
                    <button type="button" class="ap-btn is-primary" ${UIEvents.attr('click', () => AccionesPendientesView._loadAndRender())}>Reintentar</button>
                </div>
            `);
        }
    },

    // Días de calendario entre hoy y una fecha (redondeado hacia arriba) —
    // negativo si la fecha ya pasó.
    _daysUntil(dateStr) {
        const todayMidnight = new Date();
        todayMidnight.setHours(0, 0, 0, 0);
        const target = new Date(dateStr);
        target.setHours(0, 0, 0, 0);
        return Math.ceil((target - todayMidnight) / (1000 * 60 * 60 * 24));
    },

    _age(dateStr) {
        const days = -this._daysUntil(dateStr);
        if (!Number.isFinite(days) || days <= 0) return { text: 'hoy', days: 0 };
        return { text: days === 1 ? 'ayer' : `${days} días`, days };
    },

    // Lo que tiene una fecha límite real: de momento, altas de profesorado.
    _getUrgentItems() {
        if (!this._cache) return [];
        return this._cache.upcomingHires
            .map(p => ({
                days: this._daysUntil(p.hireDate),
                title: `Alta de ${`${p.firstName || ''} ${p.lastName || ''}`.trim()}`,
                sub: new Date(p.hireDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' }),
                onclick: () => App.navigate('profesor/' + p.id)
            }))
            .sort((a, b) => a.days - b.days);
    },

    // Requests store only the item id: the inventory name, or the id if it is not available.
    _itemName(item) {
        return item.name || this._inventoryNames?.get(item.itemId) || item.itemId;
    },

    _items(value) {
        try { const list = typeof value === 'string' ? JSON.parse(value) : value; return Array.isArray(list) ? list : []; } catch { return []; }
    },

    // Un único sitio que dice, para cada tipo, de dónde salen sus filas y qué hace su botón.
    _getSections() {
        const { pendingEnrollments, pendingLeads, pendingRequests, pendingOrders, payroll, reviewer, freshDocs } = this._cache;
        const money = n => `${Math.round(Number(n) || 0).toLocaleString('es-ES')} €`;
        const statusText = { pending: 'Por revisar', interview: 'Entrevista', payment: 'Falta el pago' };
        const toAdmissions = () => { if (typeof StudentView !== 'undefined') StudentView.currentSection = 'admissions'; App.navigate('alumnado/admissions'); };
        const sections = [
            { key: 'admisiones', label: 'Solicitudes por revisar', link: ['Admisiones', toAdmissions], list: pendingEnrollments,
              row: en => ({ who: [en.nombre, en.apellidos].filter(Boolean).join(' ') || en.email || 'Solicitante',
                  what: [en.tipoFormacion, statusText[en.status]].filter(Boolean).join(' · '), date: en.createdAt,
                  search: `${en.nombre || ''} ${en.apellidos || ''} ${en.email || ''} ${en.tipoFormacion || ''}`,
                  action: en.status === 'payment' ? 'Ver pago' : 'Revisar', go: en.status === 'payment' ? () => App.navigate('pre-matricula') : toAdmissions }) },
            { key: 'leads', label: 'Contactos sin contestar', link: ['Contactos', () => App.navigate('leads')], list: pendingLeads,
              row: l => ({ who: l.nombre || l.email || 'Contacto', what: l.interes || 'Sin interés indicado', date: l.createdAt,
                  search: `${l.nombre || ''} ${l.email || ''} ${l.interes || ''}`, action: 'Contestar', go: () => App.navigate('leads') }) },
            { key: 'material', label: 'Material por entregar', link: ['Almacén', () => App.navigate('almacen')], list: pendingRequests,
              row: req => {
                  const items = this._items(req.items).map(i => `${i.quantity}× ${this._itemName(i)}`).join(', ') || 'Varios artículos';
                  return { who: req.studentName || 'Alumno', what: items, date: req.createdAt, search: `${req.studentName || ''} ${items}`, action: 'Entregar', go: () => App.navigate('almacen') };
              } },
            { key: 'compras', label: 'Compras por firmar', link: ['Almacén', () => App.navigate('almacen')], list: pendingOrders,
              row: o => ({ who: o.reason || 'Orden de compra', what: `${o.requesterName || 'Sin solicitante'} · ${money(o.totalCost)} · ${this._items(o.items).length} artículos`,
                  date: o.createdAt, search: `${o.requesterName || ''} ${o.reason || ''}`, action: 'Firmar', go: () => App.navigate('almacen') }) }
        ];
        if (payroll) {
            // Cada nómina espera desde el día 28 de su mes.
            sections.unshift({ key: 'nominas', label: 'Nóminas sin subir', link: ['Nóminas', () => App.navigate('profesores/nominas')],
                list: payroll.rows.map(r => ({ ...r, name: DashboardView._professorName(r.id) })),
                row: p => ({ who: p.name || 'Persona', what: `${p.monthName}${typeof NominasUpload !== 'undefined' && NominasUpload.payLabel(p.id) ? ` · ${NominasUpload.payLabel(p.id).toLowerCase()}` : ''}`,
                    date: `${p.since}T12:00:00`, search: `${p.name || ''} ${p.monthName}`, action: 'Subir', go: () => App.navigate('profesores/nominas') }) });
        }
        if (reviewer) {
            sections.push({ key: 'justificantes', label: 'Justificantes sin abrir', link: ['Justificantes', () => App.navigate('justificantes')], list: freshDocs,
                row: d => ({ who: JustificantesView.ownerName(d), what: `${d.professorId ? 'Personal' : 'Alumnado'}${d.type ? ` · ${String(d.type).toLowerCase()}` : ''}`,
                    date: d.date || d.createdAt, search: `${JustificantesView.ownerName(d)} ${d.fileName || ''}`, action: 'Abrir', go: () => App.navigate('justificantes') }) });
        }
        // Cada fila con su antigüedad; dentro de cada tarjeta, lo que más espera primero, y las
        // tarjetas ordenadas por lo más antiguo que tienen.
        return sections.map(s => {
            const rows = s.list.map(item => { const r = s.row(item); return { ...r, age: this._age(r.date) }; }).sort((a, b) => b.age.days - a.age.days);
            return { ...s, rows, oldest: rows.length ? rows[0].age.days : -1 };
        }).sort((a, b) => b.oldest - a.oldest);
    },

    _renderShell() {
        const contentArea = document.getElementById('acciones-pendientes-content');
        if (!contentArea || !this._cache) return;

        const sections = this._getSections();
        const urgentItems = this._getUrgentItems();
        const total = sections.reduce((sum, s) => sum + s.rows.length, 0) + urgentItems.length;
        const busy = sections.filter(s => s.rows.length);
        const oldest = busy.length ? Math.max(...busy.map(s => s.oldest)) : 0;

        SafeHTML.set(contentArea, `
            <div class="ap-head">
                <div class="ap-head-text">
                    <a href="/dashboard" class="ap-back" ${UIEvents.attr('click', event => { event.preventDefault(); App.navigate('dashboard'); })}>← Inicio</a>
                    <h1 class="ap-title">Acciones pendientes</h1>
                    <p class="ap-sub">${total === 0 ? 'Todo al día: no hay nada esperando' : `${total} ${total === 1 ? 'cosa' : 'cosas'} en ${busy.length + (urgentItems.length ? 1 : 0)} ${busy.length + (urgentItems.length ? 1 : 0) === 1 ? 'tipo' : 'tipos'}`}${oldest > 0 ? ` · la más antigua espera desde hace ${oldest} ${oldest === 1 ? 'día' : 'días'}` : ''}</p>
                </div>
                <label class="ap-search">
                    <span class="ap-sr">Buscar en lo pendiente</span>
                    <i class="fas fa-search" aria-hidden="true"></i>
                    <input type="search" id="ap-search-input" placeholder="Buscar persona, artículo…" autocomplete="off">
                </label>
            </div>

            ${urgentItems.length ? `
                <section class="ap-dated" aria-label="Con fecha">
                    <strong class="ap-dated-title">Con fecha</strong>
                    ${urgentItems.map(u => `
                        <button type="button" class="ap-dated-item ${u.days <= 3 ? 'is-critical' : ''}" ${UIEvents.attr('click', u.onclick)}>
                            <span class="ap-dated-days">${u.days === 0 ? 'Hoy' : u.days === 1 ? 'Mañana' : `${u.days} días`}</span>
                            <span><strong>${Utils.sanitizeHTML(u.title)}</strong> · ${Utils.sanitizeHTML(u.sub)}</span>
                        </button>`).join('')}
                </section>` : ''}

            <div id="ap-sections-container"></div>
        `);

        this._attachToolbarListeners();
        this._renderSections();
    },

    _attachToolbarListeners() {
        const searchInput = document.getElementById('ap-search-input');
        if (!searchInput) return;
        searchInput.value = this.searchQuery;
        searchInput.addEventListener('input', (e) => {
            this.searchQuery = e.target.value;
            this._renderSections();
        });
    },

    // Repinta SOLO #ap-sections-container aplicando la búsqueda — se llama en
    // cada tecla del buscador, así que nunca toca el <input> en sí (si no,
    // perdería el foco y el cursor en cada letra).
    _renderSections() {
        const container = document.getElementById('ap-sections-container');
        if (!container || !this._cache) return;

        const sections = this._getSections();
        const q = Utils.normalizeString(this.searchQuery.trim());
        const cards = sections.map(s => ({ ...s, shown: q ? s.rows.filter(r => Utils.normalizeString(`${r.who} ${r.search}`).includes(q)) : s.rows }));
        // Every box stays on screen, also when it is clear (user request 2026-10-25); the ones with
        // something waiting come first.
        const ordered = [...cards.filter(c => c.shown.length), ...cards.filter(c => !c.shown.length)];

        SafeHTML.set(container, `
            <div class="ap-grid">
                ${ordered.map(c => {
                    const open = !!this._expanded[c.key] || !!q;
                    const visible = open ? c.shown : c.shown.slice(0, this.VISIBLE);
                    return `
                        <section class="ap-card" id="ap-section-${c.key}" aria-labelledby="ap-title-${c.key}">
                            <div class="ap-card-head">
                                <h2 class="ap-card-title" id="ap-title-${c.key}">${Utils.sanitizeHTML(c.label)}</h2>
                                <span class="ap-count ${c.shown.length ? '' : 'is-clear'}">${c.shown.length}</span>
                                <button type="button" class="ap-link" ${UIEvents.attr('click', c.link[1])}>${Utils.sanitizeHTML(c.link[0])} <span aria-hidden="true">→</span></button>
                            </div>
                            <div class="ap-rows ${open && c.shown.length > this.VISIBLE ? 'is-open' : ''}">
                                ${!c.shown.length ? `<p class="ap-clear-row"><i class="fas fa-check" aria-hidden="true"></i> ${q && c.rows.length ? 'Nada coincide con la búsqueda' : 'Al día'}</p>` : ''}
                                ${visible.map(r => `
                                    <div class="ap-row">
                                        <span class="ap-row-text"><strong>${Utils.sanitizeHTML(r.who)}</strong><span title="${Utils.sanitizeHTML(r.what)}">${Utils.sanitizeHTML(r.what)}</span></span>
                                        <span class="ap-age ${r.age.days >= 7 ? 'is-old' : ''}">${r.age.text}</span>
                                        <button type="button" class="ap-btn is-primary is-small" ${UIEvents.attr('click', r.go)}>${Utils.sanitizeHTML(r.action)}</button>
                                    </div>`).join('')}
                            </div>
                            ${!q && c.shown.length > this.VISIBLE ? `
                                <button type="button" class="ap-link ap-more" aria-expanded="${open}" ${UIEvents.attr('click', () => { this._expanded[c.key] = !open; this._renderSections(); })}>
                                    ${open ? 'Ver menos' : `Ver los ${c.shown.length}`}
                                </button>` : ''}
                        </section>`;
                }).join('')}
            </div>`);
    }
};

window.AccionesPendientesView = AccionesPendientesView;
